import React from "react";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  type IconButtonProps,
  type SxProps,
  type Theme,
} from "@mui/material";
import { CallEndIcon, MicOffIcon, MicOnIcon } from "../../../assets/icon";
import { MicVolumeHalo } from "../../mic-volume-halo/mic-volume-halo";
import {
  getRoleplayEndCallFrameSx,
  getRoleplayEndCallIconButtonSx,
  getRoleplayMicFabPressWrapperSx,
  getRoleplayMicFrameSx,
  getRoleplayMicIconButtonSx,
  getRoleplayMicScalePadSx,
  getRoleplayMicScalePadding,
  getRoleplayPushToTalkLabelSx,
  ROLEPLAY_MIC_PRESS_SCALE,
  ROLEPLAY_VIDEO_IDLE_MIC_SCALE,
  ROLEPLAY_VIDEO_MIC_PRESS_SCALE,
  roleplayControlsRowSx,
  roleplayVideoControlsSx,
} from "../styles";

type PushToTalkHandlers = Pick<
  IconButtonProps,
  "onPointerDown" | "onPointerUp" | "onPointerLeave" | "onPointerCancel"
>;

interface RoleplayCallControlsProps {
  buttonPx: number;
  iconPx: number;
  isPushPressed: boolean;
  onEndCall: () => void;
  mediaStream?: MediaStream | null;
  pushButtonRef?: React.Ref<HTMLButtonElement>;
  pushToTalkHandlers?: PushToTalkHandlers;
  buttonSx?: SxProps<Theme>;
  endButtonSx?: SxProps<Theme>;
  showLabel?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  layout?: "default" | "video";
}

export const RoleplayCallControls: React.FC<RoleplayCallControlsProps> = ({
  buttonPx,
  iconPx,
  isPushPressed,
  onEndCall,
  mediaStream = null,
  pushButtonRef,
  pushToTalkHandlers = {},
  buttonSx,
  endButtonSx,
  showLabel = true,
  isDisabled = false,
  isLoading = false,
  layout = "default",
}) => {
  const isVideoLayout = layout === "video";
  const micUnavailable = isDisabled || isLoading;
  const pressScale = isVideoLayout
    ? ROLEPLAY_VIDEO_MIC_PRESS_SCALE
    : ROLEPLAY_MIC_PRESS_SCALE;
  const micScale = isVideoLayout ? ROLEPLAY_VIDEO_IDLE_MIC_SCALE : 1;
  const micButtonPx = Math.round(buttonPx * micScale);
  const micIconPx = Math.round(iconPx * micScale);
  const endButtonPx = isVideoLayout
    ? Math.round(buttonPx * 0.85)
    : buttonPx;
  const endIconPx = Math.round(
    (isVideoLayout ? iconPx * 0.9 : iconPx) * 0.65,
  );
  const micScalePadding = getRoleplayMicScalePadding(micButtonPx, pressScale);

  const endCallControl = (
    <Box sx={getRoleplayEndCallFrameSx(isVideoLayout)}>
      <IconButton
        onClick={onEndCall}
        sx={getRoleplayEndCallIconButtonSx(endButtonPx, endButtonSx)}
        title="End call"
      >
        <CallEndIcon
          style={{
            width: endIconPx,
            height: endIconPx,
            color: "#FFB3AA",
          }}
        />
      </IconButton>
    </Box>
  );

  const micControl = (
    <Box sx={getRoleplayMicFrameSx(isVideoLayout, micButtonPx, pressScale)}>
      <Box sx={getRoleplayMicScalePadSx(micScalePadding)}>
        <Box sx={getRoleplayMicFabPressWrapperSx(isPushPressed, pressScale)}>
          {isPushPressed ? (
            <MicVolumeHalo
              stream={mediaStream}
              active={isPushPressed}
              buttonPx={micButtonPx}
              color="34, 197, 94"
            />
          ) : null}
          <IconButton
            ref={pushButtonRef}
            {...pushToTalkHandlers}
            disabled={micUnavailable}
            sx={getRoleplayMicIconButtonSx(
              isPushPressed,
              micButtonPx,
              buttonSx,
              micUnavailable,
            )}
            title="Push and hold to talk"
          >
            {isLoading ? (
              <CircularProgress size={micIconPx} sx={{ color: "#06B6D4" }} />
            ) : isPushPressed ? (
              <MicOnIcon
                style={{
                  width: micIconPx,
                  height: micIconPx,
                  color: "currentColor",
                }}
              />
            ) : (
              <MicOffIcon
                style={{
                  width: micIconPx,
                  height: micIconPx,
                  color: "currentColor",
                }}
              />
            )}
          </IconButton>
        </Box>
      </Box>
      {showLabel ? (
        <Typography sx={getRoleplayPushToTalkLabelSx(micButtonPx, pressScale)}>
          PUSH &amp; HOLD TO TALK
        </Typography>
      ) : null}
    </Box>
  );

  if (isVideoLayout) {
    return (
      <Box sx={roleplayVideoControlsSx(micButtonPx, pressScale)}>
        {micControl}
        {endCallControl}
      </Box>
    );
  }

  return (
    <Box sx={roleplayControlsRowSx}>
      {endCallControl}
      {micControl}
    </Box>
  );
};
